import { useEffect, useMemo, useState } from 'react';
import Nav from '../../components/Nav';
import VoitureAnnonce from '../../components/VoitureAnnonce';
import { useAxios } from '../../utils/hook/useAxios';
import { useUser } from '../../utils/hook/UserContext';

const STATUS_FILTERS = [
  ['1', 'Toutes'],
  ['2', 'Actives'],
  ['3', 'En attente'],
  ['4', 'Sans annonce'],
  ['5', 'Désactivées'],
];

function AccountVoitures() {
  const axios = useAxios();
  const [voitures, setVoitures] = useState([]);
  const [annonceMap, setAnnonceMap] = useState({}); // { vehicleId: annonceStatus | "none" }
  const [statusFilter, setStatusFilter] = useState('1'); // "1"=toutes, "2"=active, "3"=pending, "4"=none, "5"=desactivee
  const [searchQuery, setSearchQuery] = useState('');
  const { user, loading, isAuthenticated } = useUser();

  useEffect(() => {
    localStorage.setItem('mode', 'host');
  }, []);

  useEffect(() => {
    const fetchVoitures = async () => {
      const response = await axios.get('/vehicles/searchbyuserid/' + user.id);
      if (!response.data) return;
      const sorted = [...response.data].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
      setVoitures(sorted);

      // Pré-charger les annonces pour le filtre par statut
      const entries = await Promise.all(
        sorted.map((v) =>
          axios
            .get('/vehicleAnnonces/getbyvehiculeid/' + v.id)
            .then(({ data }) => [v.id, data?.[0]?.status ?? 'none'])
            .catch(() => [v.id, 'none'])
        )
      );
      setAnnonceMap(Object.fromEntries(entries));
    };
    fetchVoitures();
  }, []);

  const filteredVoitures = useMemo(() => {
    return voitures.filter((v) => {
      // Filtre texte
      const modelLabel =
        `${v.VehicleModel?.marque ?? ''} ${v.VehicleModel?.modele ?? ''}`.toLowerCase();
      if (searchQuery && !modelLabel.includes(searchQuery.toLowerCase())) return false;

      // Filtre statut
      if (statusFilter !== '1') {
        const s = annonceMap[v.id]; // "1"=active, "0"=pending, autre=désactivée, "none"=pas d'annonce
        if (statusFilter === '2' && s !== '1' && s !== 1) return false;
        if (statusFilter === '3' && s !== '0' && s !== 0) return false;
        if (statusFilter === '4' && s !== 'none') return false;
        if (statusFilter === '5' && (s === '1' || s === 1 || s === '0' || s === 0 || s === 'none'))
          return false;
      }
      return true;
    });
  }, [voitures, annonceMap, statusFilter, searchQuery]);

  const isFiltered = statusFilter !== '1' || Boolean(searchQuery);

  return (
    <main className="mc-home mc-account-page">
      <div className="container mc-container">
        <Nav />

        <div className="mc-account-shell">
          <div className="mc-account-head">
            <h1 className="mc-account-title">Mes voitures</h1>
            <p className="mc-account-count" aria-live="polite">
              <strong>{filteredVoitures.length}</strong> voiture
              {filteredVoitures.length !== 1 ? 's' : ''}
              {isFiltered ? ' (filtrées)' : ''}
            </p>
          </div>

          {/* Barre filtres */}
          <div className="mc-toolbar">
            <div className="mc-search">
              <ion-icon name="search-outline"></ion-icon>
              <input
                className="mc-input"
                type="search"
                aria-label="Rechercher un modèle"
                placeholder="Rechercher un modèle…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  type="button"
                  className="mc-search-clear"
                  aria-label="Effacer la recherche"
                  onClick={() => setSearchQuery('')}
                >
                  <ion-icon name="close-outline"></ion-icon>
                </button>
              )}
            </div>

            <div className="mc-chips" role="group" aria-label="Filtrer par statut d'annonce">
              {STATUS_FILTERS.map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  className="mc-chip"
                  aria-pressed={statusFilter === value}
                  onClick={() => setStatusFilter(value)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Liste */}
          {filteredVoitures.length === 0 ? (
            <div className="mc-empty">
              <span className="mc-empty-icon">
                <ion-icon name="car-sport-outline"></ion-icon>
              </span>
              <h2>Aucune voiture ne correspond</h2>
              <p>Modifiez votre recherche ou vos filtres pour afficher d'autres voitures.</p>
              {isFiltered && (
                <button
                  type="button"
                  className="mc-btn mc-btn--secondary mc-btn--sm"
                  onClick={() => {
                    setStatusFilter('1');
                    setSearchQuery('');
                  }}
                >
                  Réinitialiser les filtres
                </button>
              )}
            </div>
          ) : (
            filteredVoitures.map((voiture) => (
              <VoitureAnnonce key={voiture.id} propsData={voiture} />
            ))
          )}
        </div>
      </div>
    </main>
  );
}

export default AccountVoitures;
